<template>
  <div
    class="w-full hidden md:flex flex-col transition-all transform duration-150 ease-in-out  bg-white z-0 h-screen sticky top-0 overflow-hidden"
    :class="[open ? 'md:w-60' : 'md:w-0 opacity-0']">

    <div class="w-full px-6 h-20 border-b flex items-center justify-between flex-shrink-0">
      <h2 class="font-semibold text-3xl font-overpass flex items-center">
        <LogoIcon v-if="! $page.props.hasAppMenu" class="h-8 text-aquamarine mr-2" />
        <span>Media</span>
      </h2>
      <button v-if="$page.props.hasAppMenu" @click="closeMenu" type="button" class="w-7 p-1 focus:outline-none">
        <svg viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg">
          <path xmlns="http://www.w3.org/2000/svg" fill="currentColor"
                d="M442 114H6a6 6 0 0 1-6-6V84a6 6 0 0 1 6-6h436a6 6 0 0 1 6 6v24a6 6 0 0 1-6 6zm0 160H6a6 6 0 0 1-6-6v-24a6 6 0 0 1 6-6h436a6 6 0 0 1 6 6v24a6 6 0 0 1-6 6zm0 160H6a6 6 0 0 1-6-6v-24a6 6 0 0 1 6-6h436a6 6 0 0 1 6 6v24a6 6 0 0 1-6 6z"></path>
        </svg>
      </button>
    </div>

    <div class="flex-1 overflow-y-scroll">
      <div class="mb-10">
        <ul class="text-payneFire mt-3">
          <inertia-link
            href="/home"
            class="flex justify-between items-center pl-6 py-3 cursor-pointer group"
            :class="{'active': $page.url.startsWith('/home') }"
          >
            <div class="flex items-center justify-between flex-grow" @click="setShowAllMedia()">
              <span class="text font-semibold text-yankFire group-hover:opacity-80">All Media</span>
              <span dusk="all-media-count" class="count text-xs rounded-full px-3 py-1 bg-smoke flex justify-center items-center mr-3">
                {{ mediaCounts.all }}
              </span>
            </div>
            <span
              class="side-border absolute right-0 inset-y-0 hidden h-full border-2 rounded-l-md border-aquamarine"></span>
          </inertia-link>
          <inertia-link
            :href="$route('search', {'filter[type]': 'graphic'})"
            class="flex justify-between items-center pl-6 py-3 h-12 cursor-pointer group"
            :class="{'active': filterIs('graphic')}"
            :only="['filters', 'media']"
          >
            <div class="flex items-center justify-between flex-grow">
              <span class="text group-hover:opacity-80">Graphics</span>
              <span class="count hidden text-xs rounded-full px-3 py-1 bg-smoke flex justify-center items-center mr-5">
                {{ mediaCounts.graphic }}
              </span>
            </div>
            <span
              class="side-border absolute right-0 inset-y-0 hidden h-full border-2 rounded-l-md border-aquamarine"></span>
          </inertia-link>
          <inertia-link
            :href="$route('search', {'filter[type]': 'video'})"
            class="flex justify-between items-center pl-6 py-3 h-12 cursor-pointer group"
            :class="{'active': filterIs('video')}"
            :only="['filters', 'media']"
          >
            <div class="flex items-center justify-between flex-grow">
              <span class="text group-hover:opacity-80">Video</span>
              <span class="count hidden text-xs rounded-full px-3 py-1 bg-smoke flex justify-center items-center mr-5">
                {{ mediaCounts.video }}
              </span>
            </div>
            <span
              class="side-border absolute right-0 inset-y-0 hidden h-full border-2 rounded-l-md border-aquamarine"></span>
          </inertia-link>

          <inertia-link
            :href="$route('collections.index')"
            class="flex justify-between items-center pl-6 py-3 h-12 cursor-pointer group"
            :class="{'active': $page.url.startsWith('/collections')}"
            :only="['filters', 'media']"
          >
            <div class="flex items-center justify-between flex-grow">
              <span class="text group-hover:opacity-80">Collections</span>
              <span class="count hidden text-xs rounded-full px-3 py-1 bg-smoke flex justify-center items-center mr-5">
                {{ mediaCounts['collections'] }}
              </span>
            </div>
            <span
              class="side-border absolute right-0 inset-y-0 hidden h-full border-2 rounded-l-md border-aquamarine"></span>
          </inertia-link>

          <inertia-link
            :href="$route('search', {'filter[type]': 'social-media'})"
            class="flex justify-between items-center pl-6 py-3 h-12 cursor-pointer group"
            :class="{'active': filterIs('social-media')}"
            :only="['filters', 'media']"
          >
            <div class="flex items-center justify-between flex-grow">
              <span class="text group-hover:opacity-80">Social Media</span>
              <span class="count hidden text-xs rounded-full px-3 py-1 bg-smoke flex justify-center items-center mr-5">
                {{ mediaCounts['social-media'] }}
              </span>
            </div>
            <span
              class="side-border absolute right-0 inset-y-0 hidden h-full border-2 rounded-l-md border-aquamarine"></span>
          </inertia-link>
          <inertia-link
            :href="$route('search', {'filter[type]': 'photo'})"
            class="flex justify-between items-center pl-6 py-3 h-12 cursor-pointer group"
            :class="{'active': filterIs('photo')}"
            :only="['filters', 'media']"
          >
            <div class="flex items-center justify-between flex-grow">
              <span class="text group-hover:opacity-80">Photos</span>
              <span class="count hidden text-xs rounded-full px-3 py-1 bg-smoke flex justify-center items-center mr-5">
                {{ mediaCounts.photo }}
              </span>
            </div>
            <span
              class="side-border absolute right-0 inset-y-0 hidden h-full border-2 rounded-l-md border-aquamarine"></span>
          </inertia-link>
          <inertia-link
            :href="$route('search', {'filter[type]': 'sermon-kit'})"
            class="flex justify-between items-center pl-6 py-3 h-12 cursor-pointer group"
            :class="{'active': filterIs('sermon-kit')}"
          >
            <div class="flex items-center justify-between flex-grow">
              <span class="text group-hover:opacity-80">Sermon Kits</span>
              <span class="count hidden text-xs rounded-full px-3 py-1 bg-smoke flex justify-center items-center mr-5">
                {{ mediaCounts['sermon-kits'] }}
              </span>
            </div>
            <span
              class="side-border absolute right-0 inset-y-0 hidden h-full border-2 rounded-l-md border-aquamarine"></span>
          </inertia-link>

          <inertia-link
            :href="$route('media.favorites.index') + '?filter[type]=all'"
            class="flex justify-between items-center pl-6 py-3 h-12 cursor-pointer group"
            :class="{'active': $page.url.startsWith('/favorites')}"
          >
            <div class="flex items-center justify-between flex-grow">
              <span class="text group-hover:opacity-80">Favorites</span>
              <span class="count hidden text-xs rounded-full px-3 py-1 bg-smoke flex justify-center items-center mr-5">
                {{ $page.props.favoritedCount || 0 }}
              </span>
            </div>
            <span
              class="side-border absolute right-0 inset-y-0 hidden h-full border-2 rounded-l-md border-aquamarine"></span>
          </inertia-link>
          <div v-if="user" class="divide-y">
            <div class="pt-3"></div>
            <div class="py-4">
              <inertia-link
                href="/my-media"
                class="flex justify-between items-center pl-6 py-3 cursor-pointer group"
                :class="{'active': $page.url.startsWith('/my-media')&& !folderId }"
              >
                <div class="w-full h-full flex items-center justify-between flex-grow" @click="setShowMyMedia()">
                  <span class="text group-hover:opacity-80 text font-semibold text-yankFire">My Media</span>
                  <div class="flex flex-row items-center justify-between">
                    <span v-if="myMediaCount > 0" dusk="all-media-count" class="count text-xs rounded-full px-3 py-1 bg-smoke flex justify-center items-center mr-3">
                      {{ myMediaCount }}
                    </span>
                    <Arrow class="mr-3" />
                  </div>
                </div>
                <span
                  class="side-border absolute right-0 inset-y-0 hidden h-full border-2 rounded-l-md border-aquamarine"></span>
              </inertia-link>
              <div v-if="$page.url.startsWith('/my-media')" class="pl-4">
                <inertia-link
                  :href="'/my-media?filter[favorited]=true'"
                  class="flex justify-between items-center pl-6 py-3 h-12 cursor-pointer group">
                  <div class="w-full h-full flex items-center justify-between flex-grow" @click="showMyMedia = false">
                    <span class="text group-hover:opacity-80">Favorites</span>
                  </div>
                  <span
                    class="side-border absolute right-0 inset-y-0 hidden h-full border-2 rounded-l-md border-aquamarine"></span>
                </inertia-link>
                <inertia-link
                  v-for="folder in getFiveFolders()" :key="folder.id"
                  :href="$route('my-media.show', folder.id)"
                  class="flex justify-between items-center pl-6 py-3 h-12 cursor-pointer group"
                >
                  <div class="w-full h-full flex items-center justify-between flex-grow" @click="showMyMedia = false">
                    <span class="text group-hover:opacity-80">{{ folder.name }}</span>
                    <span class="count hidden text-xs rounded-full px-3 py-1 bg-smoke flex justify-center items-center mr-5">
                      {{ folder.items ? folder.items.length : 0 }}
                    </span>
                  </div>
                  <span
                    class="side-border absolute right-0 inset-y-0 hidden h-full border-2 rounded-l-md border-aquamarine"></span>
                </inertia-link>
                <inertia-link
                  v-if="folderCounts > 5 && !insideFolder"
                  href="/my-media"
                  class="flex justify-between items-center pl-6 py-3 h-12 cursor-pointer group"
                  :class="{'active': $page.url.startsWith('/my-media') && !folderId}"
                  :only="['filters', 'media']"
                >
                  <div class="w-full h-full flex items-center justify-between flex-grow" @click="folderId = null">
                    <span class="text group-hover:opacity-80">+ {{ folderCounts - 5 }} more</span>
                  </div>
                  <span
                    class="side-border absolute right-0 inset-y-0 hidden h-full border-2 rounded-l-md border-aquamarine"></span>
                </inertia-link>
              </div>
            </div>
            <div class="pb-3"></div>
          </div>
          <!-- <inertia-link
            v-if="user"
            :href="$route('scheduler.posts.index')"
            class="flex justify-between items-center pl-6 py-3 h-12 cursor-pointer group"
            :class="{'active': $page.url.startsWith('/scheduler')}"
          >
            <div class="flex items-center justify-between flex-grow" @click="hideMedia()">
              <span class="text group-hover:opacity-80">Scheduler</span>
              <span class="count hidden text-xs rounded-full px-3 py-1 bg-smoke flex justify-center items-center mr-5">
                {{ $page.props.postsCount || 0 }}
              </span>
            </div>
            <span
              class="side-border absolute right-0 inset-y-0 hidden h-full border-2 rounded-l-md border-aquamarine"></span>
          </inertia-link> -->
          <!-- <inertia-link
            v-if="user"
            :href="$route('social-settings.show', 'settings')"
            class="flex justify-between items-center pl-6 py-3 h-12 cursor-pointer group"
            :class="{'active': $page.url.startsWith('/social-settings')}"
          >
            <div class="flex items-center justify-between flex-grow" @click="hideMedia()">
              <span class="text group-hover:opacity-80">Settings</span>
            </div>
            <span
              class="side-border absolute right-0 inset-y-0 hidden h-full border-2 rounded-l-md border-aquamarine"></span>
          </inertia-link> -->
        </ul>
      </div>
    </div>

    <div v-if="user && ! $page.props.hasAppMenu" class="h-16 bg-white flex-shrink-0 flex items-center pl-6 border-t border-platinumFire">
      <img :src="user.profileImageUrl" class="h-9 w-9 object-cover rounded-full" />
      <div class="ml-2 leading-none flex flex-col justify-center">
        <h4 class="text-xs font-medium text-yankHot">
          {{ user.name }}
        </h4>
        <div class="space-x-2 flex items-center mt-1">
          <inertia-link :href="$route('profile-settings.show', 'details')" class="text-2xs text-yankMiddle">
            Profile Settings
          </inertia-link>
          <span aria-hidden="true" clas="text-gray-500">&middot;</span>
          <inertia-link :href="$route('logout')" class="text-2xs text-yankMiddle">
            Logout
          </inertia-link>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import LogoIcon from '@/components/svg/LogoIcon'
import Arrow from '@/components/svg/Arrow';

export default {
  name: 'Filters',

  props: {
    open: Boolean,
  },

  components: {LogoIcon, Arrow},

  data() {
    return {
      menuOpen: this.open,
      showAllMedia: false,
      showMyMedia: false,
      folderId: null,
    }
  },

  computed: {
    user() {
      return this.$page.props.user
    },

    collections() {
      return this.$page.props.featuredCollections
    },

    mediaCounts() {
      return this.$page.props.mediaCounts
    },

    folderCounts() {
      return this.$page.props.totalFoldersCounts;
    },

    filters() {
      return this.$page.props.filters
    },

    myMediaCount(){
      return this.$page.props.count ?? 0;
    },

    myMediaFolderCount(){
      return this.$page.props.totalFolders ? this.$page.props.totalFolders.length : 0;
    },

    folders() {
      if(this.$page.props.totalFolders && this.$page.props.totalFolders.data) {
        return this.$page.props.totalFolders.data;
      } else {
        return this.$page.props.totalFolders;
      }
    },

    insideFolder() {
      return this.$page.props.totalFolders && !this.$page.props.totalFolders.data;
    }
  },

  methods: {
    filterIs(name) {
      return this?.filters?.type == name
    },

    closeMenu() {
      this.menuOpen = false
      this.$emit('toggle', false)
    },

    setShowAllMedia(){
      this.showMyMedia = false;
      this.showAllMedia = !this.showAllMedia;
      this.folderId = null;
    },

    setShowMyMedia(){
      this.showAllMedia = false;
      this.showMyMedia = !this.showMyMedia;
      this.folderId = null;
    },

    getFiveFolders(){
      return this.folders.length > 5 ? this.folders.slice(0,5) : this.folders;
    },

    hideMedia(){
      this.showAllMedia = false;
      this.showMyMedia = false;
    }
  }
}
</script>

<style scoped>
.active {
  @apply relative;
}

.active span.text {
  @apply font-bold text-aquamarine;
}

.active span.side-border,
.active span.count {
  @apply block;
}
</style>
